<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div class="back-link" @click="$router.go(-1)">
          <font-awesome-icon icon="angle-left" class="back-icon" />
          <span>Back</span>
        </div>
        <div>
          <h5 class="page-title">Bulk Customer Upload</h5>
          <p class="page-sub">Import multiple billing customers at once via Excel</p>
        </div>
      </div>
  
      <!-- Content Grid -->
      <div class="upload-grid">
  
        <!-- LEFT: Upload Card -->
        <div class="card">
  
          <!-- Step 1: Download template -->
          <div class="step-block">
            <div class="step-badge">1</div>
            <div>
              <div class="step-title">Download the template</div>
              <div class="step-sub">Use our pre-formatted Excel template to ensure your data imports correctly.</div>
            </div>
          </div>
  
          <button
            type="button"
            class="btn-download"
            :disabled="downloading"
            @click.stop="downloadFile('/billing/customer/download/sample', 'Billing_Customer_Upload_Template.xlsx')"
          >
            <font-awesome-icon :icon="downloading ? 'spinner' : 'download'" :spin="downloading" />
            {{ downloading ? 'Downloading...' : 'Download Template' }}
          </button>
  
          <div class="divider"></div>
  
          <!-- Step 2: Billing group -->
          <div class="step-block">
            <div class="step-badge">2</div>
            <div>
              <div class="step-title">Select billing group(s)</div>
              <div class="step-sub">Customers will be assigned to all selected groups on import.</div>
            </div>
          </div>
  
          <div class="form-group">
            <Multiselect
              v-model="group_id"
              label="name"
              :options="group_arr"
              placeholder="Select billing group(s)..."
              mode="tags"
              :searchable="true"
              :create-option="false"
              :close-on-select="false"
              class="multiselect-styled"
            />
            <p v-if="formErrors.group_id" class="field-error">{{ formErrors.group_id }}</p>
          </div>
  
          <div class="divider"></div>
  
          <!-- Step 3: File upload -->
          <div class="step-block">
            <div class="step-badge">3</div>
            <div>
              <div class="step-title">Upload your file</div>
              <div class="step-sub">Only <strong>.xlsx</strong> or <strong>.xls</strong> files are accepted.</div>
            </div>
          </div>
  
          <!-- File input (always visible, styled as dropzone) -->
          <div
            class="dropzone"
            :class="{ 'dropzone--active': isDragging, 'dropzone--filled': !!import_file_name }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <div v-if="!import_file_name" class="dropzone-empty">
              <div class="dropzone-icon">
                <font-awesome-icon icon="file-excel" />
              </div>
              <p class="dropzone-label">Drag &amp; drop your file here</p>
              <p class="dropzone-hint">or click the button below to browse</p>
            </div>
  
            <div v-else class="dropzone-filled">
              <div class="file-icon-wrap">
                <font-awesome-icon icon="file-excel" />
              </div>
              <div class="file-info">
                <div class="file-name">{{ import_file_name }}</div>
                <div class="file-size">{{ import_file_size }}</div>
              </div>
              <button type="button" class="file-remove" @click="clearFile" title="Remove file">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>
  
          <!-- Separate visible browse button — NOT inside the dropzone -->
          <div v-if="!import_file_name" class="browse-wrap">
            <input
              type="file"
              id="fileUploadInput"
              accept=".xlsx,.xls,.csv"
              style="display:none"
              @change="onFileChange"
            />
            <label for="fileUploadInput" class="btn-browse">
              <font-awesome-icon icon="folder-open" />
              Browse File
            </label>
          </div>
  
          <!-- Validation errors -->
          <div v-if="hasErrors" class="error-box">
            <div class="error-box-title">
              <font-awesome-icon icon="exclamation-circle" class="me-2" /> Please fix the following errors:
            </div>
            <ul class="error-list">
              <li v-for="(error, key) in validationErrors" :key="key">
                {{ Array.isArray(error) ? error[0] : error }}
              </li>
            </ul>
          </div>
  
          <!-- Upload button -->
          <button
            type="button"
            class="btn-upload"
            :disabled="loading || !import_file_name"
            @click="importCustomers"
          >
            <font-awesome-icon v-if="loading" icon="spinner" spin />
            <font-awesome-icon v-else icon="upload" />
            {{ loading ? 'Uploading...' : 'Upload Customers' }}
          </button>
  
        </div>
  
        <!-- RIGHT: Instructions Card -->
        <div class="card instructions-card">
          <div class="instructions-header">
            <div class="instructions-icon">
              <font-awesome-icon icon="info-circle" />
            </div>
            <div>
              <div class="instructions-title">Upload Instructions</div>
              <div class="instructions-sub">Follow these steps to ensure a successful import</div>
            </div>
          </div>
  
          <div class="instruction-list">
            <div class="instruction-item" v-for="item in instructions" :key="item.step">
              <div class="instruction-num">{{ item.step }}</div>
              <div>
                <div class="instruction-label">{{ item.label }}</div>
                <div class="instruction-desc" v-html="item.desc"></div>
              </div>
            </div>
          </div>
  
          <!-- Required columns -->
          <div class="columns-block">
            <div class="columns-title">Required Columns</div>
            <div class="columns-grid">
              <div class="column-tag column-tag--required">
                <font-awesome-icon icon="check-circle" class="me-1" /> CUSTOMER_NAME
              </div>
              <div class="column-tag column-tag--required">
                <font-awesome-icon icon="check-circle" class="me-1" /> EMAIL
              </div>
              <div class="column-tag column-tag--required">
                <font-awesome-icon icon="check-circle" class="me-1" /> PHONE_NUMBER
              </div>
              <div class="column-tag column-tag--optional">
                <font-awesome-icon icon="minus-circle" class="me-1" /> ADDRESS
              </div>
            </div>
            <div class="columns-legend">
              <span class="legend-dot legend-dot--required"></span> Required &nbsp;&nbsp;
              <span class="legend-dot legend-dot--optional"></span> Optional
            </div>
          </div>
  
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { ref, computed, onMounted } from 'vue';
  import { useReusable } from '../../../lib/reusable';
  import Multiselect from '@vueform/multiselect';
  
  export default {
    name: 'UploadBillingCustomers',
  
    components: { Header, Multiselect },
  
    setup() {
      const { downloading, downloadFile } = useReusable();
      const toast   = useToast();
      const router  = useRouter();
  
      const import_file      = ref(null);
      const import_file_name = ref('');
      const import_file_size = ref('');
      const group_id         = ref([]);
      const group_arr        = ref([]);
      const loading          = ref(false);
      const isDragging       = ref(false);
      const validationErrors = ref([]);
      const formErrors       = ref({});
  
      const hasErrors = computed(() => {
        return Array.isArray(validationErrors.value)
          ? validationErrors.value.length > 0
          : Object.keys(validationErrors.value).length > 0;
      });
  
      const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      };
  
      const setFile = (file) => {
        if (!file) return;
        import_file.value      = file;
        import_file_name.value = file.name;
        import_file_size.value = formatFileSize(file.size);
        validationErrors.value = [];
      };
  
      const onFileChange = (e) => setFile(e.target.files[0]);
  
      const onDrop = (e) => {
        isDragging.value = false;
        setFile(e.dataTransfer.files[0]);
      };
  
      const clearFile = () => {
        import_file.value      = null;
        import_file_name.value = '';
        import_file_size.value = '';
        const input = document.getElementById('fileUploadInput');
        if (input) input.value = '';
      };
  
      const importCustomers = () => {
        formErrors.value = {};
  
        if (!group_id.value.length) {
          formErrors.value.group_id = 'Please select at least one billing group.';
          return;
        }
        if (!import_file.value) {
          toast.warning('Please select a file to upload.');
          return;
        }
  
        loading.value = true;
        const formData = new FormData();
        formData.append('import_file', import_file.value);
        group_id.value.forEach(id => formData.append('group_id[]', id));
  
        axios.post('/billing/customer/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
          .then(() => {
            toast.success('Customers imported successfully!');
            router.push('/manage-billing-customers');
          })
          .catch((error) => {
            const status = error?.response?.status;
            if (error?.message === 'Network Error') {
              clearFile();
              toast.error('Network error — please re-select your file and try again.');
            } else if (status === 422) {
              validationErrors.value = error.response.data.errors ?? [];
              toast.warning(error.response.data.message ?? 'Validation failed.');
            } else if (status === 400 || status === 403) {
              toast.error(error.response.data.message);
            } else {
              toast.error('An error occurred. Please try again.');
            }
          })
          .finally(() => { loading.value = false; });
      };
  
      const getBillingGroups = async () => {
        try {
          const { data } = await axios.get('/billing/group/active');
          group_arr.value = data.data.map(g => ({ value: g.id, name: g.name }));
        } catch (error) {
            console.log(error.response?.data?.message);
        }
      };
  
      onMounted(getBillingGroups);
  
      const instructions = [
        {
          step: 1,
          label: 'Download the template',
          desc: 'Use the Download Template button above to get the correct Excel format.',
        },
        {
          step: 2,
          label: 'Fill in customer data',
          desc: 'Complete all required columns. Do not rename or reorder the column headers.',
        },
        {
          step: 3,
          label: 'Phone number formatting',
          desc: 'Set the <strong>PHONE_NUMBER</strong> column to <strong>TEXT</strong> format in Excel to preserve leading zeros.',
        },
        {
          step: 4,
          label: 'Email addresses',
          desc: 'Each email must be unique and valid. Duplicate emails will be skipped.',
        },
        {
          step: 5,
          label: 'Address (optional)',
          desc: 'The address field is optional and can be left blank.',
        },
      ];
  
      return {
        downloading, downloadFile,
        import_file, import_file_name, import_file_size,
        group_id, group_arr,
        loading, isDragging,
        validationErrors, formErrors, hasErrors,
        onFileChange, onDrop, clearFile,
        importCustomers,
        instructions,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Layout ──────────────────────────────────────────────────────────── */
  .page-header { display:flex; align-items:flex-start; gap:16px; margin-bottom:24px; flex-wrap:wrap; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  .back-link { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:6px 10px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; transition:all .15s; white-space:nowrap; align-self:flex-start; margin-top:2px; }
  .back-link:hover { background:#f4f7fb; color:#1a2b45; }
  .back-icon { font-size:12px; }
  
  .upload-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; align-items:start; }
  @media (max-width:900px) { .upload-grid { grid-template-columns:1fr; } }
  
  /* ── Cards ───────────────────────────────────────────────────────────── */
  .card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:28px; }
  
  /* ── Steps ───────────────────────────────────────────────────────────── */
  .step-block { display:flex; align-items:flex-start; gap:14px; margin-bottom:14px; }
  .step-badge { width:26px; height:26px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
  .step-title { font-size:14px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
  .step-sub   { font-size:13px; color:#7a90b0; }
  
  .divider { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }
  
  /* ── Download button ─────────────────────────────────────────────────── */
  .btn-download { width:100%; padding:11px 16px; background:#f4f7fb; color:#004AAD; border:1px solid #c5d6f5; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px; transition:all .15s; margin-bottom:0; }
  .btn-download:hover:not(:disabled) { background:#e8f0fb; }
  .btn-download:disabled { opacity:.6; cursor:not-allowed; }
  
  /* ── Form group ──────────────────────────────────────────────────────── */
  .form-group { margin-bottom:0; }
  .field-error { font-size:12px; color:#e03535; margin-top:6px; }
  
  /* ── Dropzone ────────────────────────────────────────────────────────── */
  .dropzone { position:relative; border:2px dashed #dde5f0; border-radius:10px; padding:28px 20px; text-align:center; cursor:pointer; transition:all .2s; background:#fafcff; margin-bottom:16px; display:block; }
  .dropzone:hover, .dropzone--active { border-color:#004AAD; background:rgba(0,74,173,.04); }
  .dropzone--filled { border-style:solid; border-color:#c5d6f5; background:#f0f5ff; padding:16px 20px; }
  .dropzone-input { position:absolute; inset:0; width:100%; height:100%; opacity:0; cursor:pointer; z-index:2; }
  
  .browse-wrap { display:flex; justify-content:center; margin-bottom:16px; margin-top:-8px; }
  .btn-browse  { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-browse:hover { background:#003b8e; }
  
  .dropzone-empty { display:flex; flex-direction:column; align-items:center; gap:6px; }
  .dropzone-icon  { width:52px; height:52px; border-radius:12px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; margin-bottom:4px; }
  .dropzone-label { font-size:14px; font-weight:600; color:#1a2b45; margin:0; }
  .dropzone-hint  { font-size:13px; color:#7a90b0; margin:0; }
  .link-text { color:#004AAD; font-weight:600; }
  
  .dropzone-filled { display:flex; align-items:center; gap:14px; text-align:left; }
  .file-icon-wrap  { width:40px; height:40px; border-radius:9px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .file-info { flex:1; min-width:0; }
  .file-name { font-size:13px; font-weight:600; color:#1a2b45; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .file-size { font-size:12px; color:#7a90b0; margin-top:2px; }
  .file-remove { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#7a90b0; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; flex-shrink:0; transition:all .15s; }
  .file-remove:hover { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  
  /* ── Error box ───────────────────────────────────────────────────────── */
  .error-box       { background:#fff5f5; border:1px solid #fecaca; border-radius:8px; padding:14px 16px; margin-bottom:16px; }
  .error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:8px; }
  .error-list      { margin:0; padding-left:18px; }
  .error-list li   { font-size:13px; color:#c02828; margin-bottom:4px; }
  
  /* ── Upload button ───────────────────────────────────────────────────── */
  .btn-upload { width:100%; padding:12px 16px; background:#004AAD; color:#fff; border:none; border-radius:8px; font-size:14px; font-weight:700; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:9px; transition:background .15s; }
  .btn-upload:hover:not(:disabled) { background:#003b8e; }
  .btn-upload:disabled { opacity:.55; cursor:not-allowed; }
  
  /* ── Instructions card ───────────────────────────────────────────────── */
  .instructions-card { background:#f8fafc; }
  .instructions-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:24px; }
  .instructions-icon   { width:40px; height:40px; border-radius:10px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .instructions-title  { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
  .instructions-sub    { font-size:13px; color:#7a90b0; }
  
  .instruction-list { display:flex; flex-direction:column; gap:16px; margin-bottom:24px; }
  .instruction-item { display:flex; align-items:flex-start; gap:12px; }
  .instruction-num  { width:22px; height:22px; border-radius:50%; background:#e4eaf2; color:#7a90b0; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
  .instruction-label { font-size:13px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
  .instruction-desc  { font-size:13px; color:#7a90b0; line-height:1.5; }
  
  /* ── Required columns ────────────────────────────────────────────────── */
  .columns-block  { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:16px; }
  .columns-title  { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:12px; }
  .columns-grid   { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
  .column-tag     { display:inline-flex; align-items:center; gap:5px; border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; font-family:monospace; }
  .column-tag--required { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  .column-tag--optional { background:#f8fafc; color:#7a90b0; border:1px solid #e4eaf2; }
  .columns-legend { font-size:12px; color:#7a90b0; display:flex; align-items:center; }
  .legend-dot     { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:5px; }
  .legend-dot--required { background:#16a34a; }
  .legend-dot--optional { background:#d1d9e8; }
  .me-1 { margin-right:4px; }
  .me-2 { margin-right:8px; }
  </style>